// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg32 — Emergency Alert control page (owner-only).
//
// Turn on a firm-wide banner that shows across every page of the client AND
// admin portals — e.g. "email is temporarily unavailable." Stored on the firm
// row; surfaced to everyone via the public firm-info payload the app already
// loads at startup. Saving here calls refresh() on FirmSettingsContext so the
// banner updates immediately, including for the owner on this very page.

import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/adminApi';
import { useFirmSettings } from '../contexts/FirmSettingsContext';
import { AlertBannerRow } from '../components/AlertBanner';

const LEVELS = [
  { key: 'info',     label: 'Info',     hint: 'General notice (grey)' },
  { key: 'warning',  label: 'Warning',  hint: 'Something is wrong (amber)' },
  { key: 'critical', label: 'Critical', hint: 'Urgent / outage (red)' },
];

const LEVEL_PILL = {
  info:     { bg: '#f5f5f5', border: '#dddddd', text: '#555555' },
  warning:  { bg: '#FAEEDA', border: '#EF9F27', text: '#633806' },
  critical: { bg: '#fdf0ef', border: '#f5c6c3', text: '#B85042' },
};

export default function AdminEmergencyAlertPage() {
  const { refresh } = useFirmSettings();

  const [form,    setForm]    = useState({ active: false, message: '', level: 'warning' });
  const [savedActive, setSavedActive] = useState(false); // last persisted on/off state
  const [updatedAt,   setUpdatedAt]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    adminApi.getFirmAlert()
      .then(data => {
        setForm({ active: !!data.active, message: data.message || '', level: data.level || 'warning' });
        setSavedActive(!!data.active);
        setUpdatedAt(data.updated_at || null);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function persist(payload, okMessage) {
    setSaving(true); setError(''); setSuccess('');
    try {
      const updated = await adminApi.saveFirmAlert(payload);
      setForm({ active: !!updated.active, message: updated.message || '', level: updated.level || 'warning' });
      setSavedActive(!!updated.active);
      setUpdatedAt(updated.updated_at || null);
      await refresh(); // push into the global context so the live banner updates now
      setSuccess(okMessage);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function handleSave(e) {
    if (e) e.preventDefault();
    if (form.active && !form.message.trim()) {
      setError('Please enter a message before turning the alert on.');
      setSuccess('');
      return;
    }
    persist(
      { active: form.active, message: form.message.trim(), level: form.level },
      form.active ? 'Alert saved — it is now showing on every portal.' : 'Alert saved.'
    );
  }

  function handleTurnOff() {
    // One-click kill: keep the message/level, just switch it off.
    persist(
      { active: false, message: form.message.trim(), level: form.level },
      'Alert turned off. Clients and staff no longer see the banner.'
    );
  }

  if (loading) {
    return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;
  }

  const previewVisible = form.message.trim().length > 0;
  const lastChanged = updatedAt ? new Date(updatedAt).toLocaleString() : null;

  return (
    <div>
      <h1 className="page-title">Emergency Alert</h1>
      <p className="page-subtitle">
        Show a banner across every page of your client and staff portals — for example, to let
        clients know email is temporarily down. This is a visual notice only; it doesn't send any
        email or text. Only owners can change it.
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Current status + one-click off */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 10, height: 10, borderRadius: '50%',
            background: savedActive ? '#1e7e5e' : '#bbb', display: 'inline-block',
          }} />
          <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 'bold', color: savedActive ? '#1e7e5e' : '#555' }}>
            {savedActive ? 'Alert is ON — currently showing to everyone' : 'Alert is OFF'}
          </span>
          {lastChanged && (
            <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>
              · last changed {lastChanged}
            </span>
          )}
        </div>
        {savedActive && (
          <button
            type="button"
            className="btn"
            onClick={handleTurnOff}
            disabled={saving}
            style={{ color: '#B85042', borderColor: '#f5c6c3' }}
          >
            Turn off now
          </button>
        )}
      </div>

      <form onSubmit={handleSave}>
        <div className="section-head">Message</div>
        <div className="card">
          {/* On/off toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => set('active', !form.active)}
              aria-pressed={form.active}
              aria-label="Toggle alert on or off"
              style={{
                width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: form.active ? '#1e7e5e' : '#ccc', position: 'relative', flexShrink: 0,
                transition: 'background 0.15s', padding: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: form.active ? 23 : 3,
                width: 20, height: 20, borderRadius: '50%', background: '#fff',
                transition: 'left 0.15s',
              }} />
            </button>
            <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, color: '#0F1838' }}>
              {form.active ? 'Show this alert to everyone' : 'Alert is hidden'}
            </span>
          </div>

          {/* Severity */}
          <div className="form-group">
            <label>Severity</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {LEVELS.map(l => {
                const active = form.level === l.key;
                const pal = LEVEL_PILL[l.key];
                return (
                  <button
                    key={l.key}
                    type="button"
                    onClick={() => set('level', l.key)}
                    title={l.hint}
                    style={{
                      fontFamily: 'Arial, sans-serif', fontSize: 12, cursor: 'pointer',
                      padding: '7px 16px', borderRadius: 3,
                      background: active ? pal.bg : '#fff',
                      border: `${active ? 1.5 : 1}px solid ${active ? pal.border : '#ddd'}`,
                      color: active ? pal.text : '#555',
                      fontWeight: active ? 'bold' : 'normal',
                    }}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message text */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Banner text</label>
            <textarea
              value={form.message}
              onChange={e => set('message', e.target.value)}
              placeholder="Email is temporarily unavailable. If you need to send us documents, please call the office and we'll arrange another way. We expect it back shortly."
              rows={3}
            />
          </div>
        </div>

        {/* Live preview */}
        <div className="section-head">Preview</div>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {previewVisible ? (
            <AlertBannerRow level={form.level} message={form.message} />
          ) : (
            <div style={{ padding: '16px', fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>
              Enter a message above to see how the banner will look.
            </div>
          )}
        </div>

        <div className="btn-row" style={{ marginTop: 20 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : (form.active ? 'Save & show alert' : 'Save')}
          </button>
        </div>
      </form>
    </div>
  );
}
